import random from 'csprng';
import _ from 'lodash';
import { Cloudinary } from '@cloudinary/url-gen';
import anim1 from '@/assets/particles/anim1.json';
import anim2 from '@/assets/particles/anim2.json';
import anim3 from '@/assets/particles/anim3.json';
import anim4 from '@/assets/particles/anim4.json';
import anim5 from '@/assets/particles/anim5.json';
import { GLOBAL_CONSTANTS } from './globalConstants';

const { EMP_START_DATE, GIT_START_DATE } = GLOBAL_CONSTANTS;

const cld = new Cloudinary({
  cloud: {
    cloudName: 'd-evil',
  },
});

/**
 * @summary Equality wrapper function on top of lodash.
 * Performs a deep comparison between two values to determine if they are equivalent.
 * @description This method supports comparing arrays, array buffers, booleans, date objects,
 * error objects, maps, numbers, Object objects, regexes, sets, strings, symbols, and typed
 * arrays. Object objects are compared by their own, not inherited, enumerable properties.
 * Functions and DOM nodes are not supported.
 * @param {any} param1 The data to be compared.
 * @param {any} param2 The other value to compare.
 * @author Papu Kumar <papuruth@gmail.com>
 * @return {boolean} Returns true if the values are equivalent, else false.
 */
export const equalityChecker = (param1, param2) => _.isEqual(param1, param2);

/**
 * @summary Check Empty wrapper function on top of lodash.
 * Performs a length check on given Object or array.
 * @description Checks if value is empty. A value is
 * considered empty unless it’s an arguments Object, array, string,
 * or jQuery-like collection with a length greater than 0 or
 * an Object with own enumerable properties.
 * @param {any} param1 The data to be checked.
 * @author Papu Kumar <papuruth@gmail.com>
 * @return {boolean} Returns true if the it's not empty, else false.
 */
export const checkEmpty = (param1) => {
  const isNum = !Number.isNaN(Number(param1));
  if (param1 === null || param1 === undefined) return true;

  if (isNum && Number(param1) > 0) return false;

  if (isNum && Number(param1) <= 0) return true;

  return _.isEmpty(param1);
};

/**
 * @summary Array Sorter.
 * @description Sorts Array and Array of objects based on given condition.
 * @param {array} data - The array or array of objects to sort.
 * @param {string} key - Sort on basis of key.
 * @param {boolean} isAsc - Flag for deciding the nature of sort.
 * @author Papu Kumar <papuruth@gmail.com>
 * @returns {array | string}  The final result after sorting or error message.
 */
export const arraySorter = (data, key, isAsc) => {
  try {
    if (!data) {
      throw Error('data is missing');
    }
    if (!key) {
      throw Error('key is missing');
    }
    if (isAsc) {
      return _.sortBy(data, [(item) => item[key]]);
    }
    return _.sortBy(data, [(item) => item[key]]).reverse();
  } catch (e) {
    return e?.message;
  }
};

/**
 * @summary Random Id Generator
 * @description Generates and returns a random id
 * @author Papu Kumar <papuruth@gmail.com>
 * @returns {string}  A random string
 */
export const randomIdGenerator = () => random(160, 32);

export const getAnimationObject = () => {
  const index = Math.floor(Math.random() * (4 - 0 + 1)) + 0;
  const animationArray = [anim1, anim2, anim3, anim4, anim5]
    .map((value) => ({ value, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ value }) => value);
  return animationArray[index];
};

export const getUpdatedYears = () => {
  const years = [];

  const currYear = new Date().getFullYear();

  years.push({ label: currYear, value: currYear });

  Array(currYear - new Date(GIT_START_DATE).getFullYear())
    .fill(null)
    .forEach((__, index) => {
      years.push({ label: currYear - (index + 1), value: currYear - (index + 1) });
    });

  return years;
};

export const getCloudinaryImage = (publicId) => cld.image(`Skills/${publicId}`);

export const getExperience = () => {
  const startDate = new Date(EMP_START_DATE);
  const currDate = new Date();

  let expInYears = currDate.getFullYear() - startDate.getFullYear();

  if (currDate.getMonth() < startDate.getMonth() || (currDate.getMonth() === startDate.getMonth() && currDate.getDate() < startDate.getDate())) {
    expInYears -= 1;
  }

  const expInMonths =
    currDate.getMonth() > startDate.getMonth() || currDate.getDate() !== startDate.getDate() || currDate.getMonth() !== startDate.getMonth();

  if (expInMonths) {
    return `${expInYears}+`;
  }

  return `${expInYears}`;
};

export const getAPIErrorPayload = (type, error, payload) => ({
  type,
  error,
  payload,
});
